import { ROUTES } from 'constants/routes';
import { getBnnChapter, getBnnTopic } from 'schools/bnn/data/bnnSyllabus';
import { getJaiminiChapter, getJaiminiTopic } from 'schools/jaimini/data/jaiminiSyllabus';
import { getKpChapter, getKpTopic } from 'schools/kp/data/kpSyllabus';
import { getLalKitabChapter, getLalKitabTopic } from 'schools/lalKitab/data/lalKitabSyllabus';
import { getNumerologyChapter, getNumerologyTopic } from 'schools/numerology/data/numerologySyllabus';
import {
  getPanchangChapter,
  getPanchangTopic,
} from 'schools/panchang/data/panchangSyllabus';
import {
  getSuryaSiddhantaChapter,
  getSuryaSiddhantaTopic,
} from 'schools/suryaSiddhanta/data/suryaSiddhantaSyllabus';

import {
  getSwaraChapter,
  getSwaraTopic,
} from 'schools/swaraVigyan/data/swaraSyllabus';

import {
  getVastuChapter,
  getVastuTopic,
} from 'schools/vastu/data/vastuSyllabus';

import {
  getTantraChapter,
  getTantraTopic,
} from 'schools/tantra/data/tantraSyllabus';

import {
  getYantraChapter,
  getYantraTopic,
} from 'schools/yantraVidya/data/yantraVidyaSyllabus';

export type SchoolId = 'jaimini' | 'bnn' | 'kp' | 'lal-kitab' | 'numerology' | 'surya-siddhanta' | 'panchang' | 'vastu' | 'swara-vigyan' | 'yantra-vidya' | 'tantra';

const SCHOOL_TOPIC_KEY_RE = /^(jaimini|bnn|kp|lal-kitab|numerology|surya-siddhanta|panchang|vastu|swara-vigyan|yantra-vidya|tantra):(.+)$/;

export function buildSchoolTopicKey(school: SchoolId, topicId: string): string {
  return `${school}:${topicId}`;
}

export function parseSchoolTopicKey(
  topicKey: string,
): { school: SchoolId; topicId: string } | null {
  const match = topicKey.match(SCHOOL_TOPIC_KEY_RE);
  if (!match) return null;
  const school = match[1];
  if (
    school !== 'jaimini' &&
    school !== 'bnn' &&
    school !== 'kp' &&
    school !== 'lal-kitab' &&
    school !== 'numerology' &&
    school !== 'surya-siddhanta' &&
    school !== 'panchang' &&
    school !== 'vastu' &&
    school !== 'swara-vigyan' &&
    school !== 'yantra-vidya' &&
    school !== 'tantra'
  ) {
    return null;
  }
  return { school, topicId: match[2] };
}

export function isSchoolLearnTopicKey(topicKey: string | null | undefined): boolean {
  return Boolean(topicKey && SCHOOL_TOPIC_KEY_RE.test(topicKey));
}

export function getSchoolLearnRoute(school: SchoolId): string {
  if (school === 'jaimini') return ROUTES.JAIMINI_LEARN;
  if (school === 'kp') return ROUTES.KP_LEARN;
  if (school === 'lal-kitab') return ROUTES.LAL_KITAB_LEARN;
  if (school === 'numerology') return ROUTES.NUMEROLOGY_LEARN;
  if (school === 'surya-siddhanta') return ROUTES.SURYA_SIDDHANTA_LEARN;
  if (school === 'panchang') return ROUTES.PANCHANG_LEARN;
  if (school === 'vastu') return ROUTES.VASTU_LEARN;
  if (school === 'swara-vigyan') return ROUTES.SWARA_VIGYAN_LEARN;
  if (school === 'yantra-vidya') return ROUTES.YANTRA_VIDYA_LEARN;
  if (school === 'tantra') return ROUTES.TANTRA_LEARN;
  return ROUTES.BNN_LEARN;
}

export function buildSchoolTopicShareUrl(school: SchoolId, topicId: string): string {
  const base = getSchoolLearnRoute(school);
  const url = new URL(base, window.location.origin);
  url.searchParams.set('topic', topicId);
  return url.toString();
}

export function navigateToSchoolLearnTopic(topicKey: string): void {
  const parsed = parseSchoolTopicKey(topicKey);
  if (!parsed) return;
  window.location.assign(buildSchoolTopicShareUrl(parsed.school, parsed.topicId));
}

export function getSchoolLearnTopicLabel(topicKey: string, showEnglish: boolean): string {
  const parsed = parseSchoolTopicKey(topicKey);
  if (!parsed) return topicKey;

  const meta =
    parsed.school === 'jaimini'
      ? getJaiminiTopic(parsed.topicId)
      : parsed.school === 'kp'
        ? getKpTopic(parsed.topicId)
        : parsed.school === 'lal-kitab'
          ? getLalKitabTopic(parsed.topicId)
          : parsed.school === 'numerology'
            ? getNumerologyTopic(parsed.topicId)
            : parsed.school === 'surya-siddhanta'
              ? getSuryaSiddhantaTopic(parsed.topicId)
              : parsed.school === 'panchang'
                ? getPanchangTopic(parsed.topicId)
                : parsed.school === 'vastu'
                  ? getVastuTopic(parsed.topicId)
                  : parsed.school === 'swara-vigyan'
                    ? getSwaraTopic(parsed.topicId)
                    : parsed.school === 'yantra-vidya'
                      ? getYantraTopic(parsed.topicId)
                      : parsed.school === 'tantra'
                        ? getTantraTopic(parsed.topicId)
                        : getBnnTopic(parsed.topicId);

  if (!meta) return parsed.topicId;
  return showEnglish ? meta.topic.title.en : meta.topic.title.hi;
}

export type SchoolLeaveDestination =
  | { type: 'hub' }
  | { type: 'module'; id: string }
  | { type: 'topic'; id: string };

function getSchoolChapterMeta(school: SchoolId, chapterId: string) {
  if (school === 'jaimini') return getJaiminiChapter(chapterId);
  if (school === 'kp') return getKpChapter(chapterId);
  if (school === 'lal-kitab') return getLalKitabChapter(chapterId);
  if (school === 'numerology') return getNumerologyChapter(chapterId);
  if (school === 'surya-siddhanta') return getSuryaSiddhantaChapter(chapterId);
  if (school === 'panchang') return getPanchangChapter(chapterId);
  if (school === 'vastu') return getVastuChapter(chapterId);
  if (school === 'swara-vigyan') return getSwaraChapter(chapterId);
  if (school === 'yantra-vidya') return getYantraChapter(chapterId);
  if (school === 'tantra') return getTantraChapter(chapterId);
  return getBnnChapter(chapterId);
}

function getSchoolTopicMeta(school: SchoolId, topicId: string) {
  if (school === 'jaimini') return getJaiminiTopic(topicId);
  if (school === 'kp') return getKpTopic(topicId);
  if (school === 'lal-kitab') return getLalKitabTopic(topicId);
  if (school === 'numerology') return getNumerologyTopic(topicId);
  if (school === 'surya-siddhanta') return getSuryaSiddhantaTopic(topicId);
  if (school === 'panchang') return getPanchangTopic(topicId);
  if (school === 'vastu') return getVastuTopic(topicId);
  if (school === 'swara-vigyan') return getSwaraTopic(topicId);
  if (school === 'yantra-vidya') return getYantraTopic(topicId);
  if (school === 'tantra') return getTantraTopic(topicId);
  return getBnnTopic(topicId);
}

/** Bilingual labels for the school page-leave prompt destination line. */
export function resolveSchoolLeaveDestinationLabels(
  school: SchoolId,
  destination: SchoolLeaveDestination,
): { en: string; hi: string } {
  if (destination.type === 'hub') {
    const enSchool = getSchoolDisplayName(school, true);
    const hiSchool = getSchoolDisplayName(school, false);
    return {
      en: `${enSchool} modules`,
      hi: `${hiSchool} मॉड्यूल`,
    };
  }
  if (destination.type === 'module') {
    const chapter = getSchoolChapterMeta(school, destination.id);
    if (chapter) {
      return { en: chapter.title.en, hi: chapter.title.hi };
    }
    return { en: destination.id, hi: destination.id };
  }
  const meta = getSchoolTopicMeta(school, destination.id);
  if (meta) {
    return { en: meta.topic.title.en, hi: meta.topic.title.hi };
  }
  return { en: destination.id, hi: destination.id };
}

export function getSchoolDisplayName(school: SchoolId, showEnglish: boolean): string {
  if (school === 'jaimini') return showEnglish ? 'Jaimini' : 'जैमिनी';
  if (school === 'kp') return showEnglish ? 'KP Astrology' : 'KP ज्योतिष';
  if (school === 'lal-kitab') return showEnglish ? 'Lal Kitab' : 'लाल किताब';
  if (school === 'numerology') return showEnglish ? 'Chaldean Numerology' : 'चाल्डियन / शॉल्डियन अंक ज्योतिष';
  if (school === 'surya-siddhanta') return showEnglish ? 'Surya Siddhanta' : 'सूर्य सिद्धान्त';
  if (school === 'panchang') return showEnglish ? 'Panchang' : 'पंचांग';
  if (school === 'vastu') return showEnglish ? 'Vastu Shastra' : 'वास्तु शास्त्र';
  if (school === 'swara-vigyan') return showEnglish ? 'Swara Vigyan' : 'स्वर विज्ञान';
  if (school === 'yantra-vidya') return showEnglish ? 'Yantra Vidya' : 'यंत्र विद्या';
  if (school === 'tantra') return showEnglish ? 'Tantra' : 'तंत्र';
  return showEnglish ? 'Bhrigu Nandi Nadi' : 'भृगु नंदी नाड़ी';
}
